import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../components/api';
import { toast } from 'react-toastify';
import { AlertCircle, ArrowLeft, Download, Plus } from 'lucide-react';

// Import des composants créés
import PaymentReceiptModal from '../components/PaymentReceiptModal';
import StudentInfoCard from '../components/StudentInfoCard';
import AcademicInfoCard from '../components/AcademicInfoCard';
import PaymentScheduleCard from '../components/PaymentScheduleCard';
import FinancialStatsCard from '../components/FinancialStatsCard';
import PaymentHistoryTable from '../components/PaymentHistoryTable';
import NewPaymentModal from '../components/NewPaymentModal';
import EditPaymentModal from '../components/EditPaymentModal';
import InscriptionHeader from '../components/InscriptionHeader';
import FinancialSummary from '../components/FinancialSummary';

const DetailsInscription = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // États
  const [inscription, setInscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVersementModal, setShowVersementModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVersement, setSelectedVersement] = useState(null);
  const [modesPaiement, setModesPaiement] = useState([]);
  const [versementLoading, setVersementLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [documents, setDocuments] = useState({
    fichier1: null,
    fichier2: null
  });
  const [versementData, setVersementData] = useState({
    montant: '',
    commentaire: '',
    mode_paiement: '',
    payment_type: ''
  });
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [unpaidOptions, setUnpaidOptions] = useState([]);

  useEffect(() => {
    fetchInscriptionDetails();
    fetchModesPaiement();
  }, [id]);

  const fetchInscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/inscription/details-inscription/${id}/`);
      if (response.data.status === 'success') {
        setInscription(response.data.data);
        
        const options = response.data.data.options || [];
        const unpaidOpts = options.filter(opt => !opt.est_paye);
        setUnpaidOptions(unpaidOpts);
      } else {
        toast.error('Erreur lors du chargement des détails');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const fetchModesPaiement = async () => {
    try {
      const response = await api.get('/inscription/liste-modes-paiement/');
      if (response.data.status === 'success') {
        setModesPaiement(response.data.data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'payment_type') {
      let newAmount = '';
      
      if (value.startsWith('OPTION-')) {
        const optionId = parseInt(value.split('-')[1]);
        const option = unpaidOptions.find(opt => opt.id === optionId);
        if (option) {
          newAmount = option.option_prix.toString();
        }
      } else if (value === 'INSCRIPTION') {
        newAmount = inscription?.frais_inscription_montant?.toString() || '';
      }
      
      setVersementData(prev => ({
        ...prev,
        [name]: value,
        montant: newAmount || prev.montant
      }));
    } else {
      setVersementData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDocumentUpload = (e, field) => {
    const file = e.target.files[0];
    setDocuments(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleDocumentRemove = (field) => {
    setDocuments(prev => ({
      ...prev,
      [field]: null
    }));
  };

  const handleVersementSubmit = async (e) => {
    e.preventDefault();
    
    if (!versementData.montant || isNaN(versementData.montant) || parseInt(versementData.montant) <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }

    if (!versementData.mode_paiement) {
      toast.error('Veuillez sélectionner un mode de paiement');
      return;
    }
    
    if (!versementData.payment_type) {
      toast.error('Veuillez sélectionner un type de paiement');
      return;
    }

    try {
      setVersementLoading(true);
      
      let lignesVersement = [];
      let optionId = null;
      
      if (versementData.payment_type === 'SCOLARITE') {
        lignesVersement = [
          {
            libelle: 'Paiement de scolarité',
            montant: parseInt(versementData.montant),
            type_ligne: 'SCOLARITE'
          }
        ];
      } else if (versementData.payment_type === 'INSCRIPTION') {
        lignesVersement = [
          {
            libelle: "Paiement des frais d'inscription",
            montant: parseInt(versementData.montant),
            type_ligne: 'INSCRIPTION'
          }
        ];
      } else if (versementData.payment_type.startsWith('OPTION-')) {
        optionId = parseInt(versementData.payment_type.split('-')[1]);
        const option = unpaidOptions.find(opt => opt.id === optionId);
        
        lignesVersement = [
          {
            libelle: `Paiement de l'option: ${option?.option_libelle || 'Option complémentaire'}`,
            montant: parseInt(versementData.montant),
            type_ligne: 'OPTION',
            option_id: optionId
          }
        ];
      }
      
      if (lignesVersement.length === 0) {
        toast.error('Erreur dans la configuration du versement');
        return;
      }
      
      const versementPayload = {
        montant: parseInt(versementData.montant),
        commentaire: versementData.commentaire || `Versement - ${new Date().toLocaleDateString()}`,
        mode_paiement: parseInt(versementData.mode_paiement),
        lignes_versement: lignesVersement,
        echeances: {}
      };
      
      const formData = new FormData();
      formData.append('data', JSON.stringify(versementPayload));
      
      if (documents.fichier1) formData.append('fichier1', documents.fichier1);
      if (documents.fichier2) formData.append('fichier2', documents.fichier2);
      
      try {
        const response = await api.post(`/inscription/versements/${id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.status === 'success') {
          toast.success('Versement effectué avec succès');
          setShowVersementModal(false);
          setVersementData({
            montant: '',
            commentaire: '',
            mode_paiement: '',
            payment_type: ''
          });
          setDocuments({
            fichier1: null,
            fichier2: null
          });
          fetchInscriptionDetails();
        } else {
          toast.error(response.data.message || 'Erreur lors du versement');
          // console.error('Erreur serveur:', response.data);
        }
      } catch (error) {
        console.error('Erreur lors de la requête:', error);
        if (error.response && error.response.data) {
          let errorMessage = 'Erreur lors du versement';
          
          if (typeof error.response.data === 'object' && error.response.data.message) {
            if (typeof error.response.data.message === 'object') {
              const errorDetails = error.response.data.message;
              const errors = [];
              
              Object.keys(errorDetails).forEach(key => {
                const fieldErrors = errorDetails[key];
                if (Array.isArray(fieldErrors)) {
                  errors.push(`${key}: ${fieldErrors.join(', ')}`);
                } else {
                  errors.push(`${key}: ${fieldErrors}`);
                }
              });
              
              errorMessage = errors.join(' | ');
            } else {
              errorMessage = error.response.data.message;
            }
          }
          
          toast.error(errorMessage);
        } else {
          toast.error('Erreur de connexion au serveur');
        }
      }
    } catch (error) {
      console.error('Erreur JavaScript:', error);
      toast.error('Une erreur est survenue lors de la préparation du versement');
    } finally {
      setVersementLoading(false);
    }
  };

  const handleEditVersement = (versement) => {
    setSelectedVersement(versement);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (formData, versementId) => {
    try {
      setEditLoading(true);
      
      if (formData.deleted) {
        const response = await api.delete(`/inscription/update-versements/${versementId}/`);
        
        if (response.data.status === 'success') {
          toast.success('Versement supprimé avec succès');
          setShowEditModal(false);
          fetchInscriptionDetails();
        } else {
          toast.error(response.data.message || 'Erreur lors de la suppression');
        }
        return;
      }
      
      const totalLignes = formData.lignes_versement.reduce((sum, ligne) => sum + parseInt(ligne.montant || 0), 0);
      if (totalLignes !== parseInt(formData.montant)) {
        toast.error(`Le total des lignes (${totalLignes}) ne correspond pas au montant global (${formData.montant})`);
        return;
      }
      
      const editPayload = {
        montant: parseInt(formData.montant),
        commentaire: formData.commentaire,
        mode_paiement: parseInt(formData.mode_paiement),
        lignes_versement: formData.lignes_versement.map(ligne => ({
          ...ligne,
          montant: parseInt(ligne.montant)
        }))
      };
      
      const response = await api.put(`/inscription/update-versements/${versementId}/`, editPayload);
      
      if (response.data.status === 'success') {
        toast.success('Versement modifié avec succès');
        setShowEditModal(false);
        fetchInscriptionDetails();
      } else {
        toast.error(response.data.message || 'Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur:', error);
      
      if (error.response && error.response.data) {
        const errorMessage = typeof error.response.data.message === 'string' 
          ? error.response.data.message 
          : 'Erreur lors de la modification du versement';
        toast.error(errorMessage);
      } else {
        toast.error('Erreur de connexion au serveur');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleShowReceipt = async (versementId) => {
    try {
      setReceiptLoading(true);
      const response = await api.get(`/inscription/versement-receipt/${versementId}/`);
      if (response.data.status === 'success') {
        // console.log('Données du reçu reçues:', response.data.data);
        // console.log('Données établissement:', response.data.data.etablissement)
        setReceiptData(response.data.data);
        setShowReceiptModal(true);
      } else {
        toast.error('Erreur lors de la génération du reçu');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la génération du reçu');
    } finally {
      setReceiptLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex justify-center items-center">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-blue-600 rounded-full"></div>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Chargement des détails</h3>
            <p className="text-gray-600">Récupération des informations de l'inscription...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!inscription) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center max-w-md mx-auto border border-red-100">
            <div className="text-red-500 mb-6">
              <AlertCircle className="mx-auto w-20 h-20" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Inscription non trouvée</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              L'inscription que vous recherchez n'existe pas ou a été supprimée de nos enregistrements.
            </p>
            <button
              onClick={() => navigate('/inscriptions')}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux inscriptions
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
        {/* Nouvelle barre de navigation élégante */}
      
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* En-tête principal moderne */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden mb-8">
            <InscriptionHeader 
              inscription={inscription} 
              onNewPaymentClick={() => setShowVersementModal(true)}
            />
            <FinancialSummary inscription={inscription} />
          </div>

          {/* Grille principale réorganisée */}
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {/* Colonne gauche - Informations */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                <StudentInfoCard student={inscription?.eleve} />
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                <AcademicInfoCard inscription={inscription} />
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                <FinancialStatsCard inscription={inscription} />
              </div>
            </div>

            {/* Colonne droite - Historique et échéances */}
            <div className="lg:col-span-2 xl:col-span-3 space-y-6">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Échéancier */}
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                  <div className="h-[600px]">
                    <PaymentScheduleCard echeances={inscription?.echeances || []} />
                  </div>
                </div>

                {/* Historique */}
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                  <div className="h-[600px]">
                    <PaymentHistoryTable 
                      versements={inscription?.versements || []} 
                      onShowReceipt={handleShowReceipt}
                      onEditVersement={handleEditVersement}
                      receiptLoading={receiptLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <NewPaymentModal 
        isOpen={showVersementModal}
        onClose={() => setShowVersementModal(false)}
        onSubmit={handleVersementSubmit}
        versementData={versementData}
        onInputChange={handleInputChange}
        modesPaiement={modesPaiement}
        documents={documents}
        onDocumentUpload={handleDocumentUpload}
        onDocumentRemove={handleDocumentRemove}
        versementLoading={versementLoading}
        studentName={`${inscription?.eleve?.prenom || ''} ${inscription?.eleve?.nom || ''}`}
        paymentOptions={unpaidOptions}
        inscription={inscription}
      />

      {showEditModal && selectedVersement && (
        <EditPaymentModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEditSubmit}
          versement={selectedVersement}
          modesPaiement={modesPaiement}
          editLoading={editLoading}
          studentName={`${inscription?.eleve?.prenom || ''} ${inscription?.eleve?.nom || ''}`}
        />
      )}

      {showReceiptModal && (
        <PaymentReceiptModal
          isOpen={showReceiptModal}
          onClose={() => setShowReceiptModal(false)}
          receiptData={receiptData}
        />
      )}
    </Layout>
  );
};

export default DetailsInscription;